import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export default function PageHero({ eyebrow, lines, description, image, children }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.25]);

  return (
    <header ref={ref} className="relative isolate flex min-h-[68vh] flex-col justify-end overflow-hidden pt-32 pb-16 lg:min-h-[76vh] lg:pb-24">
      {image && (
        <motion.div style={{ y }} className="absolute inset-0 -z-10" aria-hidden="true">
          <img src={image} alt="" className="h-[130%] w-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-brand-teal/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-slate via-brand-slate/40 to-brand-slate/70" />
        </motion.div>
      )}
      <div className="grid-lines absolute inset-0 -z-10 opacity-60" aria-hidden="true" />
      <div className="mx-auto w-full max-w-7xl px-5 sm:px-8">
        <motion.p
          style={{ opacity }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-brand-ice"
          data-testid="page-hero-eyebrow"
        >
          <span className="mr-3 inline-block h-px w-8 translate-y-[-3px] bg-brand-cyan" aria-hidden="true" />
          {eyebrow}
        </motion.p>
        <h1 className="mt-6 font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight sm:text-7xl lg:text-8xl" data-testid="page-hero-title">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block will-change-transform"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        {description && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="page-hero-description"
          >
            {description}
          </motion.p>
        )}
        {children}
      </div>
    </header>
  );
}
